import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { getGestioneOperativaReparti } from '../../../../utils/api/aggiornamentoGestioneOperativaApi';
import { getResponsabiliSicurezzaEsercizio } from '../../../../utils/api/responsabili_sicurezza_api';
import {
  aggiungiContattoAlReparto,
  aggiungiContattoAllaStruttura,
  getAnagraficaContatti,
  getConfigurazioneDigest,
  rimuoviContattoDalReparto,
  rimuoviContattoDallaStruttura,
} from '../../../../utils/api/iot_notifiche_api';
import AggiungiRecapitoModal from './AggiungiRecapitoModal';
import AvvisoEscalationSenzaDestinatari from './AvvisoEscalationSenzaDestinatari';
import DigestNotificheCard from './DigestNotificheCard';
import ElencoRecapitiScope from './ElencoRecapitiScope';
import {
  COPERTURA_NON_NOTA,
  SCOPE_REPARTO,
  SCOPE_STRUTTURA,
  contattiAssegnabiliAlloScope,
  normalizzaAnagraficaContatti,
  repartiConRecapiti,
  responsabiliConIndirizzo,
  ricordaRepartiDeiRecapiti,
} from './contattiNotificheUtils';

const useStyles = makeStyles((theme) => ({
  sezione: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  blocco: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
  },
  titolo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    textAlign: 'left',
  },
  descrizione: {
    color: theme.palette.text.secondary,
  },
  errore: {
    color: theme.palette.error.main,
    fontWeight: 600,
    textAlign: 'left',
  },
  nota: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    textAlign: 'left',
  },
}));

const ANAGRAFICA_VUOTA = {
  struttura: [],
  reparto: [],
  copertura: COPERTURA_NON_NOTA,
};

const RICEVE_STRUTTURA =
  'Riceve l’escalation di una deviazione non presa in carico entro un’ora, e il riepilogo via email.';

const RICEVE_REPARTO =
  'Riceve la notifica appena una deviazione si apre su un controllo di questo reparto.';

const VUOTO_STRUTTURA =
  'Nessun recapito di struttura: l’escalation di questo esercizio non ha destinatari.';

const VUOTO_REPARTO =
  'Nessun recapito: la notifica di questo reparto non raggiungerebbe nessuno, e l’escalation partirebbe dopo un’ora verso i contatti di struttura.';

// Until a read has settled there is nothing to assert about this esercizio.
// «Nessun recapito» and «non è stato possibile leggere» are both claims about a
// configuration that has not arrived yet, and on a slow backend they would tell
// a technician to go and fix a screen that is only loading.
const LETTURA_RECAPITI = 'Lettura dei recapiti in corso…';
const LETTURA_REPARTI = 'Lettura dei reparti di questo esercizio in corso…';

// «Questo esercizio non ha reparti configurabili» and «i reparti di questo
// esercizio non sono elencabili da qui» are two different statements about the
// esercizio, and a technician acts differently on each. Only the reparti read
// tells them apart: without a published gestione operativa there is no list to
// read, and what stands on screen are the reparti a recapito already named.
const REPARTI_NON_ELENCABILI =
  'I reparti di questo esercizio non sono elencabili da qui: senza una gestione operativa pubblicata sono mostrati soltanto i reparti che hanno già un recapito.';

// The reparti read leaves out the ones created in the draft (BSS-1832), so a
// reparto just added in Gestione Operativa is missing here until it is made
// operative; without this note it would look lost.
const SOLO_REPARTI_OPERATIVI =
  'I nuovi reparti verranno mostrati in questa pagina solo dopo aver cliccato su "Rendi le modifiche operative in app".';

const NESSUN_REPARTO =
  'Nessun reparto configurabile: le notifiche di questo esercizio hanno il solo scope struttura.';

// A failed read is named by what could not be read, and the server's sentence
// follows it: `ApiError` falls back to a generic «Si è verificato un errore», so
// showing only the error would leave the technician looking at a screen with no
// recipients on it and no idea which of the three reads produced the silence.
const letturaFallita = (soggetto, errore) =>
  `${soggetto}${errore?.message ? ` ${errore.message}` : ''}`;

// A refused write is the opposite case: the refusal carries the readable
// sentence (`wrapFetchWithDetailedErrors`), and prefixing it would put the
// language of the protocol in front of the description of what happened.
const scritturaFallita = (errore) =>
  errore?.message ||
  'Non è stato possibile aggiornare i destinatari delle notifiche.';

/**
 * Backoffice half of US 16 and 17: the technician configures the recipients of
 * the IoT notifications as contact lists per scope — reparto and struttura —
 * and reads the configuration of the daily digest.
 *
 * Three things this screen deliberately does not do:
 * - it does not name a ruolo or a permesso, because a contatto is a recapito
 *   with a scope and not a user with a permission (US 17). No field here takes
 *   part in an authorisation check: the protection is the tenant, as in the rest
 *   of the panel;
 * - it does not offer to create a recapito or register a user: the anagrafica of
 *   the recapiti is BSDEVPLAT-125's table, and no route of this contract creates
 *   a row in it. What it does offer, for an esercizio that has no recapito at
 *   all, are the responsabili sicurezza of the esercizio as the *source of an
 *   address*: the server resolves a `contattoId` against `responsabile_sicurezza`
 *   before `contatto_notifica`, so the row the PUT writes is a recapito like any
 *   other and grants nothing. The read behind it — `getResponsabiliSicurezzaEsercizio`
 *   — sits outside the six routes of the contract, as `getGestioneOperativaReparti`
 *   already does;
 * - it does not offer to change the digest frequency, which lives in a single
 *   configuration point and moves with an environment variable and a redeploy.
 *
 * Every write is followed by a fresh read of the anagrafica instead of patching
 * the copy on screen. The routes are idempotent, so no write can be lost, but
 * this esercizio has more than one writer — another technician on another pod,
 * and the one-off backfill of BSDEVPLAT-144 — and the one thing that must not be
 * inferred locally is `copertoDaContattiDiStruttura`: it is the server's own
 * answer to «does the escalation reach anybody», and deciding it from the list
 * held here would raise a false alarm over a contact somebody else just added.
 */
const ContattiNotificheSection = ({ esercizioId, gestioneOperativaId }) => {
  const classes = useStyles();
  const [anagrafica, setAnagrafica] = useState(ANAGRAFICA_VUOTA);
  const [erroreAnagrafica, setErroreAnagrafica] = useState(null);
  const [caricamentoAnagrafica, setCaricamentoAnagrafica] = useState(true);
  const [digest, setDigest] = useState(null);
  const [caricamentoDigest, setCaricamentoDigest] = useState(true);
  const [reparti, setReparti] = useState([]);
  const [erroreReparti, setErroreReparti] = useState(null);
  const [caricamentoReparti, setCaricamentoReparti] = useState(true);
  const [responsabili, setResponsabili] = useState([]);
  // The reparti a group has already been shown for. Without it, removing the last
  // recapito of a reparto takes the group — and the only «Aggiungi recapito» that
  // addresses it — off the screen for good wherever the reparti read gives
  // nothing. The memory lasts as long as the mount; see `ricordaRepartiDeiRecapiti`.
  const [repartiVisti, setRepartiVisti] = useState(() => new Map());
  const [erroreScrittura, setErroreScrittura] = useState(null);
  const [scritturaInCorso, setScritturaInCorso] = useState(false);
  // The scope the modal is adding to: `null` when closed. One piece of state and
  // not an `open` flag next to a target, which can disagree.
  const [scopeInAggiunta, setScopeInAggiunta] = useState(null);

  const caricaAnagrafica = useCallback(() => {
    if (esercizioId == null || esercizioId === '') {
      setCaricamentoAnagrafica(false);
      return Promise.resolve();
    }

    setCaricamentoAnagrafica(true);
    return getAnagraficaContatti(esercizioId)
      .then((risposta) => {
        const letta = normalizzaAnagraficaContatti(risposta);
        setAnagrafica(letta);
        setRepartiVisti((precedenti) =>
          ricordaRepartiDeiRecapiti(precedenti, letta.reparto),
        );
        setErroreAnagrafica(null);
      })
      .catch((errore) => {
        // The lists go back to empty *and* the error is shown: an old copy left
        // on screen would be read as the current configuration.
        setAnagrafica(ANAGRAFICA_VUOTA);
        setErroreAnagrafica(
          letturaFallita(
            'Non è stato possibile leggere i destinatari delle notifiche di questo esercizio.',
            errore,
          ),
        );
      })
      .finally(() => setCaricamentoAnagrafica(false));
  }, [esercizioId]);

  useEffect(() => {
    caricaAnagrafica();
  }, [caricaAnagrafica]);

  useEffect(() => {
    let attivo = true;
    getConfigurazioneDigest()
      .then((risposta) => {
        if (attivo) setDigest(risposta);
      })
      .catch(() => {
        // The digest is read-only and independent: losing it costs the sentence
        // about the frequency, not the configuration of the recipients.
        if (attivo) setDigest(null);
      })
      .finally(() => {
        if (attivo) setCaricamentoDigest(false);
      });
    return () => {
      attivo = false;
    };
  }, []);

  useEffect(() => {
    let attivo = true;
    if (gestioneOperativaId == null) {
      // No published gestione operativa means no reparti to configure — a state
      // of this esercizio, read and settled, not a read still in flight.
      setReparti([]);
      setCaricamentoReparti(false);
      return undefined;
    }

    setCaricamentoReparti(true);
    // soloPubblicati: a reparto created inside the draft only has a placeholder
    // id, and the contact routes answer 400 to anything but a persisted one.
    getGestioneOperativaReparti(gestioneOperativaId, { soloPubblicati: true })
      .then((risposta) => {
        if (!attivo) return;
        setReparti(risposta?.reparti ?? []);
        setErroreReparti(null);
      })
      .catch((errore) => {
        if (!attivo) return;
        setReparti([]);
        setErroreReparti(
          letturaFallita(
            'Non è stato possibile leggere i reparti di questo esercizio: i recapiti per reparto non sono configurabili adesso.',
            errore,
          ),
        );
      })
      .finally(() => {
        if (attivo) setCaricamentoReparti(false);
      });

    return () => {
      attivo = false;
    };
  }, [gestioneOperativaId]);

  // The fourth read, independent of the other three on purpose: losing it costs
  // the extra candidates of the picker, not the reading of who is notified today.
  useEffect(() => {
    let attivo = true;
    if (esercizioId == null || esercizioId === '') return undefined;

    getResponsabiliSicurezzaEsercizio(esercizioId)
      .then((risposta) => {
        if (attivo) setResponsabili(Array.isArray(risposta) ? risposta : []);
      })
      .catch(() => {
        if (attivo) setResponsabili([]);
      });

    return () => {
      attivo = false;
    };
  }, [esercizioId]);

  const eseguiScrittura = (richiesta) => {
    setScritturaInCorso(true);
    setErroreScrittura(null);
    return richiesta
      .then(() => {
        setScopeInAggiunta(null);
        return caricaAnagrafica();
      })
      .catch((errore) => {
        setErroreScrittura(scritturaFallita(errore));
      })
      .finally(() => setScritturaInCorso(false));
  };

  const rimuovi = (contatto) =>
    eseguiScrittura(
      contatto.scope === SCOPE_STRUTTURA
        ? rimuoviContattoDallaStruttura(esercizioId, contatto.id)
        : rimuoviContattoDalReparto(contatto.scopeId, contatto.id),
    );

  const aggiungi = (contattoId) =>
    eseguiScrittura(
      scopeInAggiunta.scope === SCOPE_STRUTTURA
        ? aggiungiContattoAllaStruttura(esercizioId, contattoId)
        : aggiungiContattoAlReparto(scopeInAggiunta.scopeId, contattoId),
    );

  const gruppiDiReparto = repartiConRecapiti(
    reparti,
    anagrafica.reparto,
    repartiVisti,
  );

  // Neither sentence about the reparti before both reads have settled: the groups
  // on screen come from the reparti read *and* from the anagrafica, so asserting
  // anything while either is in flight describes a screen that is only loading.
  const letturaRepartiConclusa = !caricamentoReparti && !caricamentoAnagrafica;

  return (
    // The rest of this screen reads at the `lg` breakpoint — «Dati esercizio
    // commerciale» renders inside a default MUI Container — and a tab that runs
    // wider than its siblings stretches the escalation banner edge to edge.
    <Container>
      <div className={classes.sezione}>
        <Paper className={classes.blocco}>
          <div className={classes.titolo}>
            <Typography variant="h5" component="h3">
              Destinatari delle notifiche IoT
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className={classes.descrizione}
            >
              Ogni recapito riceve le notifiche dello scope su cui si trova —
              reparto o struttura. Un recapito è un indirizzo a cui la notifica
              arriva, non un accesso a questo pannello.
            </Typography>
          </div>

          {!caricamentoAnagrafica && (
            <AvvisoEscalationSenzaDestinatari
              copertura={anagrafica.copertura}
            />
          )}

          {erroreAnagrafica && (
            <Typography
              variant="body2"
              component="p"
              className={classes.errore}
            >
              {erroreAnagrafica}
            </Typography>
          )}

          {erroreScrittura && !scopeInAggiunta && (
            <Typography
              variant="body2"
              component="p"
              className={classes.errore}
            >
              {erroreScrittura}
            </Typography>
          )}

          <ElencoRecapitiScope
            titolo="Struttura"
            riceve={RICEVE_STRUTTURA}
            contatti={anagrafica.struttura}
            messaggioVuoto={
              caricamentoAnagrafica ? LETTURA_RECAPITI : VUOTO_STRUTTURA
            }
            etichettaAggiungi="Aggiungi recapito allo scope struttura"
            scritturaInCorso={scritturaInCorso}
            onAggiungi={() => setScopeInAggiunta({ scope: SCOPE_STRUTTURA })}
            onRimuovi={rimuovi}
          />
        </Paper>

        <Paper className={classes.blocco}>
          <div className={classes.titolo}>
            <Typography variant="h5" component="h3">
              Recapiti per reparto
            </Typography>
          </div>

          {erroreReparti && (
            <Typography
              variant="body2"
              component="p"
              className={classes.errore}
            >
              {erroreReparti}
            </Typography>
          )}

          {caricamentoReparti && (
            <Typography variant="body2" component="p" className={classes.nota}>
              {LETTURA_REPARTI}
            </Typography>
          )}

          {letturaRepartiConclusa && gestioneOperativaId == null && (
            <Typography variant="body2" component="p" className={classes.nota}>
              {REPARTI_NON_ELENCABILI}
            </Typography>
          )}

          {gestioneOperativaId != null && (
            <Typography variant="body2" component="p" className={classes.nota}>
              {SOLO_REPARTI_OPERATIVI}
            </Typography>
          )}

          {letturaRepartiConclusa &&
          gestioneOperativaId != null &&
          gruppiDiReparto.length === 0 ? (
            <Typography variant="body2" component="p" className={classes.nota}>
              {NESSUN_REPARTO}
            </Typography>
          ) : (
            gruppiDiReparto.map((gruppo) => (
              <ElencoRecapitiScope
                key={gruppo.repartoId}
                titolo={`Reparto ${gruppo.repartoNome}`}
                riceve={RICEVE_REPARTO}
                contatti={gruppo.contatti}
                messaggioVuoto={
                  caricamentoAnagrafica ? LETTURA_RECAPITI : VUOTO_REPARTO
                }
                etichettaAggiungi={`Aggiungi recapito al reparto ${gruppo.repartoNome}`}
                scritturaInCorso={scritturaInCorso}
                onAggiungi={() =>
                  setScopeInAggiunta({
                    scope: SCOPE_REPARTO,
                    scopeId: gruppo.repartoId,
                    nome: `Reparto ${gruppo.repartoNome}`,
                  })
                }
                onRimuovi={rimuovi}
              />
            ))
          )}
        </Paper>

        <Paper className={classes.blocco}>
          <DigestNotificheCard
            digest={digest}
            caricamento={caricamentoDigest}
          />
        </Paper>

        {scopeInAggiunta && (
          <AggiungiRecapitoModal
            open
            titoloScope={scopeInAggiunta.nome ?? 'Struttura'}
            nessunRecapitoCensito={
              anagrafica.struttura.length === 0 &&
              anagrafica.reparto.length === 0 &&
              responsabiliConIndirizzo(responsabili).length === 0
            }
            assegnabili={contattiAssegnabiliAlloScope(
              anagrafica,
              scopeInAggiunta,
              responsabili,
            )}
            erroreServer={erroreScrittura}
            salvataggioInCorso={scritturaInCorso}
            onAggiungi={aggiungi}
            onClose={() => {
              setScopeInAggiunta(null);
              setErroreScrittura(null);
            }}
          />
        )}
      </div>
    </Container>
  );
};

ContattiNotificheSection.propTypes = {
  esercizioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  // The published gestione operativa is where the reparti of this esercizio live.
  gestioneOperativaId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

export default ContattiNotificheSection;
